import { useEffect, useRef } from "react";

const CaptchaDraggable = () => {
    const dragging = useRef(false);
    const offsetY = useRef(0);
    const badgeRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null;
        let cleanupBadge: (() => void) | null = null;

        const setupCaptcha = () => {
            const captcha = document.querySelector(
                ".grecaptcha-badge"
            ) as HTMLElement | null;

            if (!captcha) {
                return false;
            }

            // Prevent setting up the same badge multiple times
            if (badgeRef.current === captcha) {
                return true;
            }

            // Cleanup previous badge listeners if any
            cleanupBadge?.();

            badgeRef.current = captcha;

            const badge = captcha;

            // Initial position - right bottom
            badge.style.setProperty("position", "fixed", "important");
            badge.style.setProperty("right", "0px", "important");
            badge.style.setProperty("bottom", "20px", "important");
            badge.style.setProperty("left", "auto", "important");
            badge.style.setProperty("top", "auto", "important");
            badge.style.setProperty("z-index", "99999", "important");

            // Cursor
            badge.style.setProperty("cursor", "grab", "important");

            const handleMouseDown = (e: MouseEvent) => {
                // Only allow left mouse button
                if (e.button !== 0) {
                    return;
                }

                e.preventDefault();

                dragging.current = true;

                // Keep badge expanded while dragging
                badge.classList.add("captcha-dragging");

                const rect = badge.getBoundingClientRect();

                offsetY.current = e.clientY - rect.top;

                badge.style.setProperty("cursor", "grabbing", "important");
            };

            const handleMouseMove = (e: MouseEvent) => {
                if (!dragging.current) {
                    return;
                }

                const rect = badge.getBoundingClientRect();

                // Only Y position changes
                let y = e.clientY - offsetY.current;

                // Keep captcha inside screen vertically
                const maxY = window.innerHeight - rect.height;

                y = Math.max(0, Math.min(y, maxY));

                badge.style.setProperty("top", `${y}px`, "important");
                badge.style.setProperty("bottom", "auto", "important");

                // IMPORTANT:
                // Don't change left/right.
                badge.style.setProperty("right", "0px", "important");
                badge.style.setProperty("left", "auto", "important");
            };

            const handleMouseUp = () => {
                if (!dragging.current) {
                    return;
                }

                dragging.current = false;

                // Remove dragging state
                badge.classList.remove("captcha-dragging");

                if (badge) {
                    badge.style.setProperty("cursor", "grab", "important");
                }
            };

            badge.addEventListener("mousedown", handleMouseDown);
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);

            // Proper cleanup for this badge
            cleanupBadge = () => {
                badge.removeEventListener("mousedown", handleMouseDown);
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);

                badge.classList.remove("captcha-dragging");

                if (badgeRef.current === badge) {
                    badgeRef.current = null;
                }

                dragging.current = false;
            };

            return true;
        };

        // Google creates the badge asynchronously
        interval = setInterval(() => {
            const found = setupCaptcha();

            if (found && interval) {
                clearInterval(interval);
                interval = null;
            }
        }, 500);

        // Component cleanup
        return () => {
            if (interval) {
                clearInterval(interval);
                interval = null;
            }

            cleanupBadge?.();
            cleanupBadge = null;

            dragging.current = false;
            badgeRef.current = null;
        };
    }, []);

    return null;
};

export default CaptchaDraggable;